import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { StatusUI, StatusEnum } from '@brandbank/portal-components';
import Box from '@mui/material/Box';
import { FeedDetailsCtx, useFeedDetails } from 'features/feeds';
import { ProductSearchProvider } from 'features/product-search';
import { BroadcastChannel } from 'broadcast-channel';
import { usePrompt } from 'hooks/useBlocker';
import FeedDetailsLayoutError from './FeedDetailsLayoutError';
import FeedDetailsNavbar from './FeedDetailsNavbar';
import { paths } from 'features/routing';

/*
    PurposeUIManagement can use this broadcast channel to
    post a message with a boolean on whether to block navigation away
    from feed details pages
*/
const bc = new BroadcastChannel('feed-details-routing');

const FeedDetailsLayout = () => {
  const { id = '' } = useParams();
  const { data, error, status } = useFeedDetails(id);

  const location = useLocation();
  const navigate = useNavigate();

  const [blockNavigation, setBlockNavigation] = useState(false);

  const purposeId = Number(id);
  const purposeName = data?.detail.purposeName || '';

  bc.onmessage = (shouldBlockNavigation: boolean) => {
    setBlockNavigation(shouldBlockNavigation);
  };

  useEffect(() => {
    if (location.pathname === `/feeds/${id}`) {
      navigate(paths.feedDetailsOverview(id), { replace: true });
    }
  }, [id, location.pathname, navigate]);

  usePrompt('Any unsaved changes will be lost', blockNavigation);

  return (
    <>
      {status === 'loading' && (
        <StatusUI splashPage status={StatusEnum.LOADING} />
      )}

      {status === 'error' && (
        <FeedDetailsLayoutError
          error={error}
          purposeId={purposeId}
          purposeName={purposeName}
        />
      )}

      {status === 'success' && (
        <>
          <FeedDetailsNavbar
            purposeId={id}
            purposeName={data.detail.purposeName || ''}
            purposeSubcode={data.detail.subscriberCode || '-'}
          />

          <Box sx={{ p: 2 }}>
            <FeedDetailsCtx.Provider value={data}>
              <ProductSearchProvider>
                <Outlet />
              </ProductSearchProvider>
            </FeedDetailsCtx.Provider>
          </Box>
        </>
      )}
    </>
  );
};

export default FeedDetailsLayout;
